import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Form, Input, Button, Card, message } from 'antd';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './index.css';

const { Content } = Layout;
const { TextArea } = Input; // 解构TextArea，和WritePost保持一致

export default function EditPost({ getPostById, onEditPost, loggedInUser, onLogout }) {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    const initEditData = () => {
      try {
        // 强制转数字，避免ID类型不匹配
        const postId = Number(id);
        if (isNaN(postId)) {
          message.error('无效的文章ID！');
          navigate('/');
          setLoading(false);
          return;
        }

        const post = getPostById(postId);
        if (!post) {
          message.error('文章不存在！');
          navigate('/');
          setLoading(false);
          return;
        }

        // 权限校验：强制转数字，避免字符串/数字匹配失败
        if (Number(post.authorId) !== Number(loggedInUser.id)) {
          message.error('你没有权限编辑该文章！');
          navigate('/');
          setLoading(false);
          return;
        }

        // 回填表单：content保留原始格式（换行/空格）
        form.setFieldsValue({
          title: post.title || '',
          summary: post.summary || '',
          content: post.content || '', // 核心：保留原始换行/空格
          tags: post.tags?.join(',') || ''
        });

        setHasPermission(true);
      } catch (err) {
        message.error('初始化编辑数据失败：' + err.message);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    initEditData();
  }, [id, getPostById, loggedInUser, form, navigate]);

  const onFinish = (values) => {
    if (!hasPermission) return;
    
    // 处理标签：去空、转数组
    const tags = values.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag);

    // 处理摘要：换行替换为空格，避免摘要显示混乱（主内容保留换行）
    const summary = values.summary || values.content.slice(0, 50).replace(/\n/g, ' ') + '...';

    // 调用编辑回调：content保留原始值，无过滤
    onEditPost(Number(id), {
      title: values.title.trim(), // 标题仍trim（避免首尾空格）
      summary: summary,
      content: values.content, // 核心：保留原始换行/空格，不做任何处理
      tags: tags
    });

    message.success('文章修改成功！');
    // 延迟跳转，确保提示语能显示
    setTimeout(() => navigate('/'), 800);
  };

  // 加载中状态优化
  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 16, color: '#1890ff' }}>加载中...</div>
      </Layout>
    );
  }

  if (!hasPermission) {
    return null;
  }

  return (
    <Layout className="edit-post-layout">
      <Header loggedInUser={loggedInUser} onLogout={onLogout} />
      <Content style={{ padding: '40px 100px', minHeight: 'calc(100vh - 120px)' }}>
        <Card 
          title="编辑文章（支持换行/空格 + Markdown）" 
          style={{ maxWidth: 800, margin: '0 auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ tags: '' }}
            size="middle"
          >
            <Form.Item
              name="title"
              label="文章标题 *"
              rules={[
                { required: true, message: '请输入文章标题' },
                { max: 100, message: '标题长度不能超过100字' },
                { pattern: /^\S.*$/, message: '标题不能以空格开头' }
              ]}
            >
              <Input 
                placeholder="修改文章标题..." 
                autoFocus 
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
            <Form.Item
              name="summary"
              label="文章摘要 *"
              rules={[
                { required: true, message: '请输入文章摘要' },
                { max: 200, message: '摘要长度不能超过200字' }
              ]}
            >
              <Input 
                placeholder="修改文章摘要（简要描述核心内容）..." 
                style={{ borderRadius: 6 }}
                maxLength={200}
                showCount
              />
            </Form.Item>
            <Form.Item
              name="tags"
              label="文章标签 *"
              rules={[{ required: true, message: '请输入标签，多个标签用逗号分隔' }]}
              tooltip={{ title: '例如：React,前端,博客（多个标签用英文逗号分隔）' }}
            >
              <Input 
                placeholder="修改标签，例如：React,前端,博客" 
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
            {/* 核心修改：内容输入框保留换行/空格 */}
            <Form.Item
              name="content"
              label="文章内容（支持换行/空格 + Markdown）*"
              rules={[{ required: true, message: '请输入文章内容' }]}
            >
              <TextArea
                placeholder="修改你的文章内容（换行/空格会被保留，支持Markdown）..."
                autoSize={{ minRows: 15, maxRows: 30 }} // 适配高度，和WritePost一致
                autoScrollIntoView={false}
                style={{ 
                  whiteSpace: 'pre-wrap', // 核心：保留输入的换行/空格
                  fontFamily: 'inherit', 
                  lineHeight: 1.8, // 优化可读性
                  borderRadius: 6
                }}
              />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit"
                size="large"
                style={{ borderRadius: 6 }}
              >
                保存修改
              </Button>
              <Button 
                onClick={() => navigate('/')} 
                style={{ marginLeft: 16, borderRadius: 6 }}
              >
                取消
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Content>
      <Footer />
    </Layout>
  );
}